/**
 * Craigslist channel panel (listing-marketing-hub US-4, BR-7, BR-12). In order: what Resida fills and
 * what the landlord still does; the signed-out contact email rule with the Resida address and Copy;
 * that replies to it arrive as leads; then the Post action (or the extension capability note) and
 * recording the post. Instruction text is kept in step with the market-on-craigslist Help article.
 */
import type { ReactNode } from 'react';
import { CopyValueBox } from '../../../pages/leasing/listing-detail/ShareCopyButtons';
import { GuideLastChecked, HelpGuideLink } from './HelpGuideLink';
import { Callout, PostingControls, StepList } from './StepList';

export const CRAIGSLIST_GUIDE_ARTICLE_ID = 'market-on-craigslist';

interface CraigslistPanelProps {
  /** The listing's Resida email address (its intake alias). */
  intakeAddress: string | null;
  /** The Post to Craigslist action, the extension capability note, or the posting-off note. */
  postAction: ReactNode;
  /** The Craigslist record-a-post form; null while posting is off. */
  recordForm: ReactNode;
  /** The listing is paused or closed: posting and copying are off, the guide stays readable (BR-1). */
  postingOff: boolean;
  /** Posted Craigslist records on this listing. */
  postedCount: number;
}

export function CraigslistPanel({ intakeAddress, postAction, recordForm, postingOff, postedCount }: CraigslistPanelProps) {
  return (
    <section
      aria-labelledby="marketing-craigslist-title"
      data-testid="marketing-panel-craigslist"
      className="flex min-w-0 flex-1 flex-col gap-5 rounded-xl border border-border bg-card p-[var(--card-padding)]"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <h2 id="marketing-craigslist-title" className="text-lg font-semibold text-foreground">
            Craigslist
          </h2>
          <p className="text-sm text-muted-foreground" data-testid="craigslist-intro">
            Resida fills Craigslist&apos;s housing form from this listing, photos included. You sign out of Craigslist,
            review the form and press Post.
          </p>
        </div>
        <HelpGuideLink articleId={CRAIGSLIST_GUIDE_ARTICLE_ID} testId="craigslist-guide-link" />
      </div>

      <StepList
        testId="craigslist-steps"
        steps={[
          {
            title: 'Sign out of Craigslist first',
            children: (
              <>
                <Callout title="Signed in? Craigslist uses your account email." testId="craigslist-signed-out-callout">
                  You can only choose a contact email when you post signed out. Sign out, then post, so replies reach
                  your Resida address.
                </Callout>
                <PostingControls off={postingOff}>
                  <CopyValueBox
                    label="Contact email to use"
                    value={intakeAddress}
                    emptyText="Address pending…"
                    testIdPrefix="craigslist-contact-email"
                  />
                </PostingControls>
                <p className="text-sm text-foreground" data-testid="craigslist-replies-note">
                  Replies to that address come to Resida and appear as leads in your pipeline.
                </p>
              </>
            ),
          },
          {
            title: 'Post the listing',
            children: (
              <>
                <p className="text-sm text-muted-foreground">
                  Opens Craigslist&apos;s housing form already filled from this listing, photos included. You review it
                  and press Post.
                </p>
                {postAction}
                {!postingOff && (
                  <p className="text-xs text-muted-foreground">If the contact email did not fill, paste it from step 1.</p>
                )}
              </>
            ),
          },
          {
            title: 'Confirm your email and record the post',
            children: (
              <>
                <p className="text-sm text-muted-foreground" data-testid="craigslist-record-copy">
                  {postingOff
                    ? 'Posting is off, but you can still record a post you made yourself with the form above. Take each post down on Craigslist, then mark it removed.'
                    : "Craigslist may email you a link to confirm the post. After it goes live, paste the post's web address here so Resida can remind you to take it down later."}
                </p>
                {recordForm}
                <p className="text-xs text-muted-foreground" data-testid="craigslist-recorded-count">
                  {postedCount === 0
                    ? 'No Craigslist posts recorded yet.'
                    : `${postedCount} Craigslist ${postedCount === 1 ? 'post' : 'posts'} recorded.`}
                </p>
              </>
            ),
          },
        ]}
      />

      <GuideLastChecked articleId={CRAIGSLIST_GUIDE_ARTICLE_ID} testId="craigslist-guide-checked" />
    </section>
  );
}
